import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faListCheck,
  faCheck,
  faTimes,
  faSpinner,
  faFilter,
  faSearch,
  faCrosshairs,
  faCheckDouble,
  faSort,
  faSortUp,
  faSortDown,
  faExclamationTriangle,
  faEye,
  faUndo
} from '@fortawesome/free-solid-svg-icons';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import blueTeamService from '../services/blueTeamService';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';

const SIEMReviewPage = () => {
  const { t } = useLanguage();
  const { darkMode: isDarkMode } = useTheme();
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [findings, setFindings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Filters
  const [search, setSearch] = useState('');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [reviewStatusFilter, setReviewStatusFilter] = useState('all');
  const [targetMatchOnly, setTargetMatchOnly] = useState(true);
  const [sortBy, setSortBy] = useState('relevanceScore');
  const [sortOrder, setSortOrder] = useState('desc');

  const dark = (cls, lightCls) => isDarkMode ? cls : lightCls;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await blueTeamService.getSIEMReviewFindings(projectId, {
        targetMatchOnly: targetMatchOnly ? 'true' : undefined,
        severity: severityFilter !== 'all' ? severityFilter : undefined,
        search: search || undefined,
        sortBy,
        sortOrder,
        reviewStatus: reviewStatusFilter !== 'all' ? reviewStatusFilter : undefined
      });
      setFindings(result.findings || []);
      setStats(result.stats || {});
    } catch (error) {
      toast.error('Failed to load SIEM review data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [projectId, targetMatchOnly, severityFilter, search, sortBy, sortOrder, reviewStatusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return faSort;
    return sortOrder === 'desc' ? faSortDown : faSortUp;
  };

  const handleConfirm = async (findingId) => {
    try {
      await blueTeamService.updateSIEMReviewStatus(findingId, 'confirmed');
      setFindings(prev => prev.map(f => f._id === findingId ? { ...f, siemReviewStatus: 'confirmed' } : f));
      if (stats) {
        setStats(prev => ({
          ...prev,
          totalConfirmed: prev.totalConfirmed + 1,
          totalPending: prev.totalPending - 1
        }));
      }
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleDismiss = async (findingId) => {
    try {
      await blueTeamService.updateSIEMReviewStatus(findingId, 'dismissed');
      setFindings(prev => prev.map(f => f._id === findingId ? { ...f, siemReviewStatus: 'dismissed' } : f));
      if (stats) {
        setStats(prev => ({
          ...prev,
          totalDismissed: prev.totalDismissed + 1,
          totalPending: prev.totalPending - 1
        }));
      }
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleResetToReview = async (findingId) => {
    try {
      await blueTeamService.updateSIEMReviewStatus(findingId, 'pending_review');
      setFindings(prev => prev.map(f => f._id === findingId ? { ...f, siemReviewStatus: 'pending_review' } : f));
      fetchData(); // Refresh stats
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleBulkAction = async (status) => {
    if (selectedIds.size === 0) return;
    setActionLoading(true);
    try {
      await blueTeamService.bulkUpdateSIEMReviewStatus(Array.from(selectedIds), status);
      toast.success(`${selectedIds.size} findings marked as ${status}`);
      setSelectedIds(new Set());
      fetchData();
    } catch (error) {
      toast.error('Bulk update failed');
    } finally {
      setActionLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === findings.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(findings.map(f => f._id)));
    }
  };

  const getSeverityBadge = (severity) => {
    const colors = {
      critical: 'bg-red-100 text-red-800',
      high: 'bg-orange-100 text-orange-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-green-100 text-green-800',
      informational: 'bg-blue-100 text-blue-800'
    };
    return colors[severity] || 'bg-gray-100 text-gray-800';
  };

  const getReviewStatusBadge = (status) => {
    const styles = {
      pending_review: dark('bg-yellow-900/30 text-yellow-400 border-yellow-700', 'bg-yellow-100 text-yellow-800 border-yellow-200'),
      confirmed: dark('bg-green-900/30 text-green-400 border-green-700', 'bg-green-100 text-green-800 border-green-200'),
      dismissed: dark('bg-gray-700 text-gray-400 border-gray-600', 'bg-gray-100 text-gray-500 border-gray-200')
    };
    const labels = {
      pending_review: t('siemReview.pendingReview') || 'Pending',
      confirmed: t('siemReview.confirmed') || 'Confirmed',
      dismissed: t('siemReview.dismissed') || 'Dismissed'
    };
    return (
      <span className={`px-2 py-0.5 text-xs rounded-full border ${styles[status] || ''}`}>
        {labels[status] || status}
      </span>
    );
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button onClick={() => navigate(`/projects/${projectId}/blue-team`)} className={`text-sm mb-2 flex items-center ${dark('text-gray-400 hover:text-gray-200', 'text-gray-500 hover:text-gray-700')}`}>
            <FontAwesomeIcon icon={faArrowLeft} className="mr-1" />
            {t('common.back')}
          </button>
          <h1 className={`text-2xl font-bold flex items-center ${dark('text-white', 'text-gray-900')}`}>
            <FontAwesomeIcon icon={faListCheck} className="mr-3 text-teal-500" />
            {t('siemReview.title') || 'SIEM Import Review'}
          </h1>
          <p className={`text-sm mt-1 ${dark('text-gray-400', 'text-gray-500')}`}>
            {t('siemReview.subtitle') || 'Review imported SIEM alerts. Only confirmed alerts will appear in Purple Team correlation.'}
          </p>
        </div>
        <Link
          to={`/projects/${projectId}/purple-team`}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center"
        >
          <FontAwesomeIcon icon={faEye} className="mr-2" />
          {t('blueTeam.purpleTeamView') || 'Purple Team'}
        </Link>
      </div>

      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className={`rounded-lg p-4 ${dark('bg-gray-800', 'bg-white')} shadow`}>
            <p className={`text-xs ${dark('text-gray-400', 'text-gray-500')}`}>{t('siemReview.totalImported') || 'Total Imported'}</p>
            <p className={`text-2xl font-bold ${dark('text-white', 'text-gray-900')}`}>{stats.totalImported}</p>
          </div>
          <div className={`rounded-lg p-4 ${dark('bg-gray-800', 'bg-white')} shadow`}>
            <p className={`text-xs text-teal-500`}>{t('siemReview.targetMatched') || 'Target Matched'}</p>
            <p className="text-2xl font-bold text-teal-600">{stats.totalTargetMatched}</p>
          </div>
          <div className={`rounded-lg p-4 ${dark('bg-gray-800', 'bg-white')} shadow`}>
            <p className={`text-xs text-yellow-500`}>{t('siemReview.pendingReview') || 'Pending Review'}</p>
            <p className="text-2xl font-bold text-yellow-600">{stats.totalPending}</p>
          </div>
          <div className={`rounded-lg p-4 ${dark('bg-gray-800', 'bg-white')} shadow`}>
            <p className={`text-xs text-green-500`}>{t('siemReview.confirmed') || 'Confirmed'}</p>
            <p className="text-2xl font-bold text-green-600">{stats.totalConfirmed}</p>
          </div>
          <div className={`rounded-lg p-4 ${dark('bg-gray-800', 'bg-white')} shadow`}>
            <p className={`text-xs ${dark('text-gray-400', 'text-gray-500')}`}>{t('siemReview.dismissed') || 'Dismissed'}</p>
            <p className={`text-2xl font-bold ${dark('text-gray-500', 'text-gray-400')}`}>{stats.totalDismissed}</p>
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div className={`rounded-lg shadow p-4 ${dark('bg-gray-800', 'bg-white')}`}>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <FontAwesomeIcon icon={faSearch} className={`absolute left-3 top-1/2 -translate-y-1/2 text-sm ${dark('text-gray-500', 'text-gray-400')}`} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('siemReview.searchPlaceholder') || 'Search title, technique, assets...'}
              className={`w-full pl-9 pr-3 py-2 rounded-md border text-sm ${dark('bg-gray-700 border-gray-600 text-white', 'bg-white border-gray-300 text-gray-900')}`}
            />
          </div>

          {/* Severity */}
          <select value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)} className={`text-sm px-3 py-2 rounded-md border ${dark('bg-gray-700 border-gray-600 text-white', 'bg-white border-gray-300')}`}>
            <option value="all">{t('common.all')} Severity</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
            <option value="informational">Info</option>
          </select>

          {/* Review status */}
          <select value={reviewStatusFilter} onChange={(e) => setReviewStatusFilter(e.target.value)} className={`text-sm px-3 py-2 rounded-md border ${dark('bg-gray-700 border-gray-600 text-white', 'bg-white border-gray-300')}`}>
            <option value="all">{t('common.all')} Status</option>
            <option value="pending_review">{t('siemReview.pendingReview') || 'Pending'}</option>
            <option value="confirmed">{t('siemReview.confirmed') || 'Confirmed'}</option>
            <option value="dismissed">{t('siemReview.dismissed') || 'Dismissed'}</option>
          </select>

          {/* Target match toggle */}
          <label className={`flex items-center text-sm cursor-pointer ${dark('text-gray-300', 'text-gray-700')}`}>
            <input type="checkbox" checked={targetMatchOnly} onChange={(e) => setTargetMatchOnly(e.target.checked)} className="mr-1.5" />
            <FontAwesomeIcon icon={faCrosshairs} className="mr-1 text-teal-500" />
            {t('siemReview.targetMatchOnly') || 'Target match only'}
          </label>
        </div>

        {/* Bulk actions */}
        {selectedIds.size > 0 && (
          <div className={`mt-3 pt-3 border-t flex items-center gap-3 ${dark('border-gray-700', 'border-gray-200')}`}>
            <span className={`text-sm ${dark('text-gray-300', 'text-gray-700')}`}>
              {selectedIds.size} {t('common.selected') || 'selected'}:
            </span>
            <button
              onClick={() => handleBulkAction('confirmed')}
              disabled={actionLoading}
              className="px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50 flex items-center"
            >
              <FontAwesomeIcon icon={faCheckDouble} className="mr-1.5" />
              {t('siemReview.confirmAll') || 'Confirm All'}
            </button>
            <button
              onClick={() => handleBulkAction('dismissed')}
              disabled={actionLoading}
              className="px-3 py-1.5 bg-gray-600 text-white text-sm rounded hover:bg-gray-700 disabled:opacity-50 flex items-center"
            >
              <FontAwesomeIcon icon={faTimes} className="mr-1.5" />
              {t('siemReview.dismissAll') || 'Dismiss All'}
            </button>
            {actionLoading && <FontAwesomeIcon icon={faSpinner} className="animate-spin text-blue-500" />}
          </div>
        )}
      </div>

      {/* Findings table */}
      <div className={`rounded-lg shadow overflow-hidden ${dark('bg-gray-800', 'bg-white')}`}>
        {loading ? (
          <div className="flex justify-center items-center h-48">
            <FontAwesomeIcon icon={faSpinner} className="animate-spin text-3xl text-blue-500" />
          </div>
        ) : findings.length === 0 ? (
          <div className={`text-center py-12 ${dark('text-gray-500', 'text-gray-400')}`}>
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-4xl mb-3" />
            <p>{t('siemReview.noFindings') || 'No SIEM imports found matching your filters.'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
            <table className={`min-w-full divide-y ${dark('divide-gray-700', 'divide-gray-200')}`}>
              <thead className={`sticky top-0 ${dark('bg-gray-700', 'bg-gray-50')}`}>
                <tr>
                  <th className="px-3 py-2 text-left">
                    <input type="checkbox" onChange={toggleSelectAll} checked={selectedIds.size === findings.length && findings.length > 0} />
                  </th>
                  <th className={`px-3 py-2 text-left text-xs font-medium uppercase cursor-pointer select-none ${dark('text-gray-400', 'text-gray-500')}`} onClick={() => handleSort('title')}>
                    Title <FontAwesomeIcon icon={getSortIcon('title')} className="ml-1" />
                  </th>
                  <th className={`px-3 py-2 text-left text-xs font-medium uppercase cursor-pointer select-none ${dark('text-gray-400', 'text-gray-500')}`} onClick={() => handleSort('severity')}>
                    Severity <FontAwesomeIcon icon={getSortIcon('severity')} className="ml-1" />
                  </th>
                  <th className={`px-3 py-2 text-center text-xs font-medium uppercase cursor-pointer select-none ${dark('text-gray-400', 'text-gray-500')}`} onClick={() => handleSort('relevanceScore')}>
                    Relevance <FontAwesomeIcon icon={getSortIcon('relevanceScore')} className="ml-1" />
                  </th>
                  <th className={`px-3 py-2 text-left text-xs font-medium uppercase ${dark('text-gray-400', 'text-gray-500')}`}>
                    Assets
                  </th>
                  <th className={`px-3 py-2 text-left text-xs font-medium uppercase ${dark('text-gray-400', 'text-gray-500')}`}>
                    Technique
                  </th>
                  <th className={`px-3 py-2 text-left text-xs font-medium uppercase cursor-pointer select-none ${dark('text-gray-400', 'text-gray-500')}`} onClick={() => handleSort('detectionTimestamp')}>
                    Timestamp <FontAwesomeIcon icon={getSortIcon('detectionTimestamp')} className="ml-1" />
                  </th>
                  <th className={`px-3 py-2 text-center text-xs font-medium uppercase ${dark('text-gray-400', 'text-gray-500')}`}>
                    Review
                  </th>
                  <th className={`px-3 py-2 text-center text-xs font-medium uppercase ${dark('text-gray-400', 'text-gray-500')}`}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y ${dark('divide-gray-700', 'divide-gray-200')}`}>
                {findings.map(finding => (
                  <tr
                    key={finding._id}
                    className={`${
                      selectedIds.has(finding._id) ? dark('bg-blue-900/20', 'bg-blue-50') : ''
                    } ${
                      finding.targetMatch ? dark('border-l-2 border-l-teal-500', 'border-l-2 border-l-teal-400') : ''
                    } ${
                      finding.siemReviewStatus === 'dismissed' ? 'opacity-50' : ''
                    }`}
                  >
                    <td className="px-3 py-2">
                      <input type="checkbox" checked={selectedIds.has(finding._id)} onChange={() => toggleSelect(finding._id)} />
                    </td>
                    <td className={`px-3 py-2 text-sm max-w-xs ${dark('text-gray-200', 'text-gray-900')}`}>
                      <Link
                        to={`/projects/${projectId}/blue-team/findings/${finding._id}`}
                        className="hover:text-blue-500 transition-colors"
                      >
                        {finding.targetMatch && <FontAwesomeIcon icon={faCrosshairs} className="mr-1 text-teal-500" />}
                        {finding.title}
                      </Link>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 text-xs rounded-full ${getSeverityBadge(finding.severity)}`}>
                        {finding.severity}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`inline-block px-2 py-0.5 text-xs rounded-full font-medium ${
                        finding.relevanceScore >= 60 ? 'bg-teal-100 text-teal-800' :
                        finding.relevanceScore >= 30 ? 'bg-yellow-100 text-yellow-800' :
                        dark('bg-gray-700 text-gray-400', 'bg-gray-100 text-gray-500')
                      }`}>
                        {finding.relevanceScore || 0}%
                      </span>
                    </td>
                    <td className={`px-3 py-2 text-xs ${dark('text-gray-400', 'text-gray-500')}`}>
                      {finding.affectedAssets?.slice(0, 2).map((a, i) => (
                        <span key={i} className={`inline-block mr-1 px-1.5 py-0.5 rounded ${dark('bg-gray-700', 'bg-gray-100')}`}>
                          {a.value}
                        </span>
                      ))}
                      {(finding.affectedAssets?.length || 0) > 2 && (
                        <span className={`text-xs ${dark('text-gray-500', 'text-gray-400')}`}>+{finding.affectedAssets.length - 2}</span>
                      )}
                    </td>
                    <td className={`px-3 py-2 text-xs ${dark('text-gray-400', 'text-gray-500')}`}>
                      {finding.suspectedTechnique?.id || '-'}
                    </td>
                    <td className={`px-3 py-2 text-xs ${dark('text-gray-400', 'text-gray-500')}`}>
                      {finding.detectionTimestamp ? format(new Date(finding.detectionTimestamp), 'MMM d, HH:mm') : '-'}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {getReviewStatusBadge(finding.siemReviewStatus)}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {finding.siemReviewStatus === 'pending_review' && (
                          <>
                            <button
                              onClick={() => handleConfirm(finding._id)}
                              className="p-1.5 text-green-600 hover:bg-green-100 rounded transition-colors"
                              title={t('siemReview.confirm') || 'Confirm'}
                            >
                              <FontAwesomeIcon icon={faCheck} />
                            </button>
                            <button
                              onClick={() => handleDismiss(finding._id)}
                              className="p-1.5 text-gray-400 hover:bg-gray-100 rounded transition-colors"
                              title={t('siemReview.dismiss') || 'Dismiss'}
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </button>
                          </>
                        )}
                        {(finding.siemReviewStatus === 'confirmed' || finding.siemReviewStatus === 'dismissed') && (
                          <button
                            onClick={() => handleResetToReview(finding._id)}
                            className={`p-1.5 rounded transition-colors ${dark('text-gray-400 hover:bg-gray-700', 'text-gray-400 hover:bg-gray-100')}`}
                            title={t('siemReview.resetToReview') || 'Reset to pending'}
                          >
                            <FontAwesomeIcon icon={faUndo} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SIEMReviewPage;
